import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { User } from '../_models/user';
import { paths } from '../shared/paths';
import { ToastrService } from 'ngx-toastr';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

@Injectable({
  providedIn: 'root'
})
export class UserService {

  constructor(private http: HttpClient, private toastr: ToastrService) { }
  private loginUrl = `${paths.apiBaseUrl}/api/${paths.login}`;
  private loginVerifyOtpUrl = `${paths.apiBaseUrl}/api/${paths.loginVerifyOtp}`;
  private loginResendOtpUrl = `${paths.apiBaseUrl}/api/${paths.loginResendOtp}`;
  private registerUrl = `${paths.apiBaseUrl}/api/${paths.register}`;
  private logoutUrl = `${paths.apiBaseUrl}/${paths.logout}`;
  private resetPasswordUrl = `${paths.apiBaseUrl}${paths.forget_password_reset}`;
  private forgetPasswordUrl = `${paths.apiBaseUrl}${paths.forget_password}`;
  public loggedIn = true;

  login(email: string, password: string): Observable<any> {
    return this.http.post<any>(this.loginUrl, { email: email, password: password }, httpOptions)
      .pipe(map(response => {
        const data = response.data || response;
        if (data.mfa_required) {
          this.storeStateToken(data.state_token);
          return { mfa_required: true };
        } else {
          this.toastr.error('Invalid credentials');
          throw new Error('Invalid credentials');
        }
      }));
  }

  verifyOtp(stateToken: string, otpCode: string): Observable<User> {
    return this.http.post<any>(this.loginVerifyOtpUrl, { state_token: stateToken, otp: otpCode }, httpOptions)
      .pipe(map(response => {
        this.clearStateToken();
        const data = response.data || response;
        const user = this.getUserFromToken(data.token);
        localStorage.setItem('user', JSON.stringify(user));
        this.loggedIn = true;
        return user;
      }));
  }

  resendOtp(stateToken: string): Observable<any> {
    return this.http.post<any>(this.loginResendOtpUrl, { state_token: stateToken }, httpOptions)
      .pipe(map(response => {
        const data = response.data || response;
        this.storeStateToken(data.state_token || response.state_token);
        return response;
      }));
  }

  getStateToken(): string {
    return sessionStorage.getItem('mfa_state_token');
  }

  getStateTokenExpiry(): number {
    return parseInt(sessionStorage.getItem('mfa_state_token_expires_at'), 10) || 0;
  }

  private storeStateToken(token: string) {
    const expiresAt = Date.now() + 5 * 60 * 1000;
    sessionStorage.setItem('mfa_state_token', token);
    sessionStorage.setItem('mfa_state_token_expires_at', expiresAt.toString());
  }

  clearStateToken() {
    sessionStorage.removeItem('mfa_state_token');
    sessionStorage.removeItem('mfa_state_token_expires_at');
  }

  logout() {
    this.loggedIn = false;
    return this.http.post(this.logoutUrl, {}, httpOptions)
  }

  getUserFromToken(token) {
    const encoded_data = token.split(".");
    let data = new User;
    if (encoded_data.length === 3) {
      data = JSON.parse(atob(encoded_data[1]));
    }
    data.token = token;
    return data;
  }

  register(data: object) {
    return this.http.post(this.registerUrl, data, httpOptions)
      .pipe(map(response => {
        const user = this.getUserFromToken(response['data'].token);
        localStorage.setItem('user', JSON.stringify(user));
        return user;
      }));
  }

  forgetPasswordReset(data: object) {
    return this.http.post(this.resetPasswordUrl, data, httpOptions).pipe(map(response => {
      const user = this.getUserFromToken(response['data'].token);
      localStorage.setItem('user', JSON.stringify(user));
      return user;
    }));
  }

  forgetPassword(data: object) {
    return this.http.post(this.forgetPasswordUrl, data, httpOptions);
  }

  get user() {
    if (localStorage.getItem('user')) {
      return JSON.parse(localStorage.getItem('user'));
    } else {
      return new User();
    }

  }
}
